"use client";

import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  HydratedAsset,
  useSplitTreatments,
  useBulkCheckMovePermissions,
  useBulkCheckDeletePermissions,
} from "@zapier/client";
import { useBetaDataTableContext } from "@zapier/design-system";
import { useTranslations } from "@zapier/i18n";
import { AssetTypes, getAssetTypeLabel } from "@zapier/utils";
import { useAssetListBulkActions } from "../../../hooks/useAssetListBulkActions";
import type { BulkSelectAction } from "../../common/Table/BulkActions/BulkActions";
import { BulkActionsBarWrapper } from "../../common/Table/BulkActions/BulkActionsBarWrapper";
import { BulkMoveModal } from "./BulkMoveModal";
import { CanvasBulkChangeOwnerModal } from "./CanvasBulkChangeOwnerModal";
import { CopyToAnotherAccountModal } from "../../zsl/CopyToAnotherAccountModal/CopyToAnotherAccountModal";
import { AssetDeleteModal } from "../AssetDeleteModal/AssetDeleteModal";
import {
  CopyCandidateAsset,
  resolveSelectedCopyAssets,
  toHydratedAsset,
} from "./bulkCopySelection";

// Mirrors CanvasBulkChangeOwnerModal's member-search debounce: avoids running
// a permission check on every keystroke-speed selection change.
const DELETE_PERMISSION_CHECK_DEBOUNCE_MS = 300;

interface BulkActionsWrapperProps {
  assetType?: AssetTypes;
  folderId?: string;
  assetsForCopyToAnotherAccount?: CopyCandidateAsset[];
  // Raw-id -> display name, used to name a single-asset move-to-trash modal.
  assetNamesById?: Record<string, string>;
  children: (props: {
    bulkSelectActions: BulkSelectAction[] | undefined;
    onCopyToAnotherAccount?: (asset: CopyCandidateAsset) => void;
  }) => ReactNode;
}

/**
 * Encapsulates the bulk-actions feature-flag check, action hook, floating bar,
 * and modal so each list page doesn't have to wire them up individually.
 *
 * Named generically because it will grow to support bulk delete, share, etc.
 *
 * Usage:
 * ```
 * <BulkActionsWrapper assetType={AssetTypes.Canvas}>
 *   {({ bulkSelectActions }) => (
 *     <CanvasesTable bulkSelectActions={bulkSelectActions} ... />
 *   )}
 * </BulkActionsWrapper>
 * ```
 */
export function BulkActionsWrapper({
  assetType,
  folderId,
  assetsForCopyToAnotherAccount,
  assetNamesById,
  children,
}: BulkActionsWrapperProps) {
  const {
    isBulkMoveAssetsEnabled,
    isDuplicateAssetsEnabled,
    isZapAndCanvasUnifiedTrashEnabled,
    isTableUnifiedTrashEnabled,
    isBulkChangeOwnerEnabled: isBulkChangeOwnerFlagOn,
    isNgzBulkMoveEnabled,
    isNgzBulkTrashEnabled,
    isNgzBulkChangeOwnerEnabled,
    isNgzBulkCopyEnabled,
  } = useSplitTreatments();
  const isNextGenZap = assetType === AssetTypes.NextGenZap;
  const t = useTranslations("bulk-delete-modal");

  const { check, ...permissionResult } = useBulkCheckMovePermissions();
  const { check: checkDelete, ...deletePermissionResult } =
    useBulkCheckDeletePermissions();
  const { selectedRows } = useBetaDataTableContext();
  const [rowCopyAssets, setRowCopyAssets] = useState<HydratedAsset[]>([]);
  const hasCopyCandidates = useMemo(
    () =>
      assetsForCopyToAnotherAccount?.some(
        (asset) => typeof asset.id === "string" && asset.id.length > 0,
      ) ?? false,
    [assetsForCopyToAnotherAccount],
  );

  const handleMoveClicked = useCallback(
    (typedIds: string[]) => {
      check(typedIds);
    },
    [check],
  );

  const handleDeleteClicked = useCallback(
    (typedIds: string[]) => {
      checkDelete(typedIds);
    },
    [checkDelete],
  );

  const handleBulkCopyClicked = useCallback(() => {
    setRowCopyAssets([]);
  }, []);

  // Next Gen Zaps gate each bulk action on its own flag; other types use the shared flags.
  const isBulkCopyEnabled =
    (isNextGenZap ? isNgzBulkCopyEnabled : isDuplicateAssetsEnabled) &&
    hasCopyCandidates &&
    !!assetType;

  // Scoped to this component's unified-trash toolbar action specifically -
  // not "can this asset type be bulk-deleted at all". Classic Zap falls
  // through to false here because it already has its own separate,
  // longstanding bulk-delete action (ZapTable's zap-delete) outside this
  // component. Table additionally needs its own flag on; Canvas bulk-delete
  // must not depend on that unrelated flag.
  const isUnifiedTrashBulkDeleteEnabled = isNextGenZap
    ? isNgzBulkTrashEnabled
    : (assetType === AssetTypes.Canvas && isZapAndCanvasUnifiedTrashEnabled) ||
      (assetType === AssetTypes.Table &&
        isZapAndCanvasUnifiedTrashEnabled &&
        isTableUnifiedTrashEnabled);

  const typedSelectedRowIds = useMemo(
    () =>
      assetType ? selectedRows.map((id) => `${assetType}:${id}`) : selectedRows,
    [assetType, selectedRows],
  );

  // Debounced check so the toolbar button disables itself once the whole
  // selection is non-deletable; the partial case is handled in the modal.
  const deletePermissionCheckTimeoutReference = useRef<NodeJS.Timeout>();
  useEffect(() => {
    if (!isUnifiedTrashBulkDeleteEnabled || typedSelectedRowIds.length === 0) {
      return;
    }

    clearTimeout(deletePermissionCheckTimeoutReference.current);
    deletePermissionCheckTimeoutReference.current = setTimeout(() => {
      checkDelete(typedSelectedRowIds);
    }, DELETE_PERMISSION_CHECK_DEBOUNCE_MS);

    return () => clearTimeout(deletePermissionCheckTimeoutReference.current);
  }, [checkDelete, isUnifiedTrashBulkDeleteEnabled, typedSelectedRowIds]);

  const isDeleteActionDisabled =
    isUnifiedTrashBulkDeleteEnabled &&
    typedSelectedRowIds.length > 0 &&
    deletePermissionResult.hasChecked &&
    !deletePermissionResult.isLoading &&
    deletePermissionResult.deletableIds.length === 0;

  const deleteActionTooltip = isDeleteActionDisabled
    ? t("buttons.delete-permission-denied-tooltip", {
        count: typedSelectedRowIds.length,
        assetTypeLabel: getAssetTypeLabel(typedSelectedRowIds),
      })
    : undefined;

  const isBulkChangeOwnerEnabled = isNextGenZap
    ? isNgzBulkChangeOwnerEnabled
    : isBulkChangeOwnerFlagOn && assetType === AssetTypes.Canvas;

  // `!!` is load-bearing for tests, not real traffic: useSplitTreatments
  // always resolves isNgzBulkMoveEnabled to a strict boolean, but a test mock
  // that omits it leaves it undefined, and useAssetListBulkActions defaults
  // isBulkMoveEnabled to true when it's undefined - without the coercion,
  // that mock would quietly turn Move on.
  const isBulkMoveEnabledForAssetType = isNextGenZap
    ? !!isNgzBulkMoveEnabled
    : isBulkMoveAssetsEnabled;

  const {
    bulkSelectActions,
    isMoveModalOpen,
    setMoveModalOpen,
    selectedTypedAssetIds,
    onMoveSettled,
    isCopyModalOpen,
    setCopyModalOpen,
    selectedTypedAssetIdsForCopy,
    isDeleteModalOpen,
    setDeleteModalOpen,
    onDeleteSettled,
    isChangeOwnerModalOpen,
    setChangeOwnerModalOpen,
    onChangeOwnerSettled,
  } = useAssetListBulkActions({
    assetType,
    onMoveClicked: handleMoveClicked,
    onCopyToAnotherAccountClicked: handleBulkCopyClicked,
    onDeleteClicked: handleDeleteClicked,
    isBulkMoveEnabled: isBulkMoveEnabledForAssetType,
    isBulkCopyEnabled,
    isBulkDeleteEnabled: isUnifiedTrashBulkDeleteEnabled,
    isBulkChangeOwnerEnabled,
    isDeleteActionDisabled,
    deleteActionTooltip,
  });

  const bulkCopyAssets = useMemo(() => {
    return resolveSelectedCopyAssets({
      assetType,
      assetsForCopyToAnotherAccount,
      selectedTypedAssetIdsForCopy,
    });
  }, [assetType, assetsForCopyToAnotherAccount, selectedTypedAssetIdsForCopy]);

  // Same per-type gate as isBulkCopyEnabled above: Next Gen Zaps read their
  // own flag, everything else reads the shared one. Keeping both checks on
  // this one ternary is what stops NGZ copy from ending up gated behind two
  // different flags once a real onCopyToAnotherAccount lands for it.
  const isRowCopyEnabledForAssetType = isNextGenZap
    ? isNgzBulkCopyEnabled
    : isDuplicateAssetsEnabled;

  const handleCopyToAnotherAccount = useCallback(
    (asset: CopyCandidateAsset) => {
      if (!isRowCopyEnabledForAssetType) {
        return;
      }

      const hydratedAsset = toHydratedAsset(asset, assetType);
      if (!hydratedAsset) {
        return;
      }

      setRowCopyAssets([hydratedAsset]);
      setCopyModalOpen(true);
    },
    [assetType, isRowCopyEnabledForAssetType, setCopyModalOpen],
  );

  const copyModalAssets =
    rowCopyAssets.length > 0 ? rowCopyAssets : bulkCopyAssets;
  const copyEntryPoint =
    rowCopyAssets.length > 0 ? "row_kebab" : "bulk_selection";
  const shouldShowCopyModal = isCopyModalOpen && copyModalAssets.length > 0;

  const activeBulkSelectActions =
    bulkSelectActions.length > 0 ? bulkSelectActions : undefined;

  // Mirrors the modal's own itemCount fallback: before the permission check
  // resolves, go off the raw selection; after, go off the deletable subset
  // (which can narrow a multi-select down to one deletable asset).
  const singleDeleteAssetName = useMemo(() => {
    if (!assetNamesById) {
      return undefined;
    }
    const { deletableIds, hasChecked, isLoading } = deletePermissionResult;
    const effectiveIds =
      hasChecked && !isLoading ? deletableIds : selectedTypedAssetIds;

    if (effectiveIds.length !== 1) {
      return undefined;
    }
    const [typedId] = effectiveIds;
    const separatorIndex = typedId.indexOf(":");
    const rawId =
      separatorIndex === -1 ? typedId : typedId.slice(separatorIndex + 1);
    return assetNamesById[rawId] ?? assetNamesById[typedId];
  }, [assetNamesById, deletePermissionResult, selectedTypedAssetIds]);

  return (
    <>
      {children({
        bulkSelectActions: activeBulkSelectActions,
        onCopyToAnotherAccount: isRowCopyEnabledForAssetType
          ? handleCopyToAnotherAccount
          : undefined,
      })}
      <BulkActionsBarWrapper />
      {isBulkMoveEnabledForAssetType && (
        <BulkMoveModal
          selectedAssetIds={selectedTypedAssetIds}
          isOpen={isMoveModalOpen}
          setIsOpen={setMoveModalOpen}
          onSettled={onMoveSettled}
          folderId={folderId}
          permissionResult={permissionResult}
        />
      )}
      {shouldShowCopyModal && (
        <CopyToAnotherAccountModal
          isOpen={isCopyModalOpen}
          setIsOpen={(isOpen) => {
            if (isOpen) {
              setCopyModalOpen(true);
              return;
            }

            setCopyModalOpen(false);
            setRowCopyAssets([]);
          }}
          assets={copyModalAssets}
          copyEntryPoint={copyEntryPoint}
        />
      )}
      {isUnifiedTrashBulkDeleteEnabled && (
        <AssetDeleteModal
          selectedAssetIds={selectedTypedAssetIds}
          isOpen={isDeleteModalOpen}
          setIsOpen={setDeleteModalOpen}
          onSettled={onDeleteSettled}
          singleAssetName={singleDeleteAssetName}
          permissionResult={deletePermissionResult}
          assetNamesById={assetNamesById}
        />
      )}
      {isBulkChangeOwnerEnabled && !isNextGenZap && (
        <CanvasBulkChangeOwnerModal
          selectedAssetIds={selectedTypedAssetIds}
          isOpen={isChangeOwnerModalOpen}
          setIsOpen={setChangeOwnerModalOpen}
          onSettled={onChangeOwnerSettled}
          folderId={folderId}
        />
      )}
    </>
  );
}
